/**
 * Data-deletion reporting for platform disconnects.
 *
 * Dependency-free on purpose: imported by the Deno edge function
 * (`../../../shared/disconnect-data-deletion.ts`) and by the app
 * (`@shared/disconnect-data-deletion`), so the two always agree on what a
 * disconnect may claim about deleted data.
 *
 * A disconnect only claims deletion for a platform whose data purge ran (LinkedIn and
 * LinkedIn Ads, see PURGE_REGISTERED_PLATFORMS). Every other platform deactivates the
 * connection and removes its stored tokens, and Mora keeps the data it synced, so it
 * never reports a deletion outcome. A LinkedIn cleanup failure is reported as
 * 'incomplete' so the user can ask us to remove what we could not.
 */

/**
 * Platforms whose disconnect runs the platform data purge. Declared here, NOT derived from
 * `platform-purge-registry.ts`: importing the registry pulls about 7 KB of registry rows into
 * the app boot bundle (this file is imported by usePlatformIntegrations). A vitest
 * (`purge-result.test.ts`, "single source of purge platforms") asserts this equals the set
 * derived from PLATFORM_PURGE_REGISTRY, so adding a platform to the registry fails CI until
 * this list follows.
 */
export const PURGE_REGISTERED_PLATFORMS = ['linkedin', 'linkedin-ads'] as const;

export function isPurgeRegisteredPlatform(platform: string): boolean {
  return (PURGE_REGISTERED_PLATFORMS as readonly string[]).includes(platform);
}

export type DataDeletionStatus =
  | 'completed'
  | 'queued'
  /** Deleted, except records Mora could not attribute to this account (left for review). */
  | 'partial'
  | 'incomplete'
  | 'not_applicable';

export interface DataDeletionReport {
  status: DataDeletionStatus;
  /** ISO date by which queued deletion must finish (only for 'queued'). */
  dueAt?: string;
  /** 'queued' only: a purge RAN and failed; Mora retries daily (not just scheduled). */
  retrying?: boolean;
  /** Tables (or 'vault') whose deletion failed (for 'incomplete'). */
  failed?: string[];
  /**
   * Stored credentials kept because another connection still uses them (omitted when 0).
   * Informational, never a failure: this connection's own credentials are gone from its row
   * and the shared secret stays until its last holder is removed. Does not change the toast copy.
   */
  retainedShared?: number;
}

/** A platform purge's outcome as the disconnect reports it. */
export interface PurgeSummary {
  status: 'completed' | 'queued' | 'partial';
  dueAt?: string;
  /** A purge ran and failed (or its request errored): it is queued for a daily retry. */
  retrying?: boolean;
}

/**
 * Pure mapping from the edge function's cleanup outcome to the report.
 * - a platform WITHOUT a purge (not in PURGE_REGISTERED_PLATFORMS) is always
 *   'not_applicable', whatever failed: the copy "This X account is no longer connected"
 *   is true, and its table failures stay in the response's cleanupSummary.warnings
 *   (and the function logs) instead of a warning toast about data we never promised
 *   to delete.
 * - any failure wins over every purge status (a failed purge may have no ledger row,
 *   so "will be deleted by ..." would be a false promise): 'incomplete' with the list.
 * - purge result provided: its status/dueAt pass through ('partial' = deleted except
 *   unattributed rows).
 * - neither: 'not_applicable'.
 */
export function summarizeDataDeletion(input: {
  platform: string;
  failures: string[];
  softDeleteFailed?: boolean;
  purge?: PurgeSummary | null;
  retainedShared?: number;
}): DataDeletionReport {
  const report = summarizeDataDeletionStatus(input);
  const retained = input.retainedShared;
  return typeof retained === 'number' && retained > 0
    ? { ...report, retainedShared: retained }
    : report;
}

function summarizeDataDeletionStatus(input: {
  platform: string;
  failures: string[];
  softDeleteFailed?: boolean;
  purge?: PurgeSummary | null;
}): DataDeletionReport {
  const { platform, failures, softDeleteFailed, purge } = input;
  if (!isPurgeRegisteredPlatform(platform)) return { status: 'not_applicable' };
  if (softDeleteFailed) {
    return { status: 'incomplete', failed: [...new Set([...failures, 'user_connections'])] };
  }
  if (failures.length > 0) return { status: 'incomplete', failed: failures };
  if (!purge) return { status: 'not_applicable' };
  if (purge.status === 'queued') {
    return {
      status: 'queued',
      ...(purge.dueAt ? { dueAt: purge.dueAt } : {}),
      ...(purge.retrying ? { retrying: true } : {}),
    };
  }
  return { status: purge.status };
}

/** Plain-language toast copy for a disconnect. Never overclaims deletion. */
export function describeDataDeletion(
  platformLabel: string,
  report: DataDeletionReport | undefined,
): { tone: 'success' | 'warning'; title: string; body: string } {
  switch (report?.status) {
    case 'completed':
      return {
        tone: 'success',
        title: `Disconnected ${platformLabel}`,
        body: `Data Mora synced from ${platformLabel} has been deleted.`,
      };
    case 'queued': {
      const due = report.dueAt ? new Date(report.dueAt) : null;
      const when =
        due && !Number.isNaN(due.getTime())
          ? `by ${due.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            })}`
          : 'within 10 days';
      if (report.retrying) {
        // The purge ran and failed: say so. The date is the Terms deadline Mora is working to.
        return {
          tone: 'warning',
          title: `Disconnected ${platformLabel}`,
          body: `We couldn't finish deleting data Mora synced from ${platformLabel} yet. Mora retries daily and it will be deleted ${when}.`,
        };
      }
      return {
        tone: 'success',
        title: `Disconnected ${platformLabel}`,
        body: `Data Mora synced from ${platformLabel} will be deleted ${when}.`,
      };
    }
    case 'partial':
      return {
        tone: 'warning',
        title: `Disconnected ${platformLabel}`,
        body: `Data Mora synced from ${platformLabel} has been deleted, except some older records that couldn't be matched to this account. We'll review and remove those.`,
      };
    case 'incomplete':
      return {
        tone: 'warning',
        title: `Disconnected ${platformLabel}, but not everything was cleaned up`,
        body: `Some data or stored credentials for this ${platformLabel} account couldn't be removed. Contact hello@mora-marketer.com and we'll remove it.`,
      };
    default:
      return {
        tone: 'success',
        title: `Disconnected ${platformLabel}`,
        body: `This ${platformLabel} account is no longer connected to Mora.`,
      };
  }
}
